// Iter 373 — Alur Cetak Mockup Rido (A5/A4 + Unduh + Konfirmasi Tandai Dicetak).
// Scope: utility komponen & helper, DIPAKAI dari PenyiapanCard (production_queue.js).
// Backend TIDAK diubah; semua fungsi pakai endpoint existing:
//   - GET  /api/orders/{number}/mockup.preview  → ambil image mockup (barcode-stamped).
//   - POST /api/orders/{number}/products/{pid}/mockup-print → tandai dicetak (idempotent).
// Preview, unduh, dan print dialog TIDAK memanggil mockup-print endpoint. Hanya
// konfirmasi manual (klik "Ya, sudah dicetak") yang memanggilnya.

import React, { useEffect, useState } from "react";
import { Printer, Download, X, FileText, AlertCircle } from "lucide-react";
import { jsPDF } from "jspdf";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// ---------------------------------------------------------------------------
// Pilihan ukuran kertas penyajian: A5 (default) atau A4. Kertas FISIK tetap A4.
// A5 landscape ditempatkan di pojok kiri-atas lembar A4 dengan margin aman.
// ---------------------------------------------------------------------------
const PAPER_PRESETS = {
  // Iter 642 — Standar Shopee (Epson L120 Gudang Rido): kertas FISIK A5 Landscape 210×148 mm, 1 file = 1 halaman.
  //   @page memaksa orientasi landscape → Chrome tidak lagi memakai A5 Portrait.
  A5L: {
    label: "A5 Landscape (210 × 148 mm)",
    desc: "Kertas A5 mendatar. Mockup diperbesar/diperkecil proporsional & dipusatkan (tidak diputar), 1 file per halaman.",
    boxWidth: 210, boxHeight: 148, pageW: 210, pageH: 148, size: "A5 landscape", safe: 5, exact: true,
  },
  // W x H dalam mm. A4 portrait = 210x297. A5 landscape = 210x148.
  A5: {
    label: "A5 (default)",
    desc: "Mockup ditempatkan di area A5 landscape pojok kiri atas lembar A4.",
    boxWidth: 210, // mm — A5 landscape width = A4 width
    boxHeight: 148, // mm — A5 landscape height
  },
  // Rido Shopee — PDF A4 Portrait 210×297 mm. Orientasi mengikuti format operator Mockup per file.
  A4R: {
    label: "A4 Portrait (210 × 297 mm)",
    desc: "PDF A4 Portrait, 1 file = 1 halaman. File A4 Landscape otomatis diputar 90°, A4 Portrait tampil normal.",
    boxWidth: 210, boxHeight: 297, pdf: true,
  },
  A4: {
    label: "A4",
    desc: "Mockup mengisi area A4 (dengan margin aman printer).",
    boxWidth: 210,
    boxHeight: 297,
  },
};

// Margin aman printer (mm) — tidak dicetak oleh kebanyakan printer rumah/kantor.
const SAFE_MARGIN_MM = 8;
// Iter 641 — CSS halaman per preset. `exact`: tinggi tetap sedikit < kertas + overflow hidden agar tidak muncul
//   halaman kosong akibat pembulatan; page break hanya di antara halaman.
const pageCss = (p) => {
  const safe = p.safe ?? SAFE_MARGIN_MM, pw = p.pageW || 210, ph = p.pageH || 297;
  return { safe, size: p.size || "A4", box: `width:${pw}mm;${p.exact ? `height:${(ph - 0.8).toFixed(1)}mm;overflow:hidden;` : `min-height:${ph}mm;`}`,
    align: p.exact ? "center" : "flex-start", bw: Math.max(10, (p.boxWidth || pw) - safe * 2), bh: Math.max(10, (p.boxHeight || ph) - safe * 2 - (p.exact ? 1 : 0)) };
};

// Ambil image mockup sebagai blob URL. Mengembalikan { url, filename, revoke }.
// Caller WAJIB panggil revoke() setelah selesai supaya tidak bocor memori.
// `source`: { url: string, fallbackName?: string } atau fungsi `() => Promise<Response>`.
async function fetchBlobFromUrl(url, fallbackName) {
  const res = await fetch(url, { credentials: "include" });
  if (!res.ok) {
    let detail = "";
    try {
      const d = await res.json();
      detail = d?.detail || "";
    } catch {}
    const err = new Error(detail || `HTTP ${res.status}`);
    err.status = res.status;
    throw err;
  }
  const blob = await res.blob();
  const cd = res.headers.get("Content-Disposition") || "";
  const match = /filename="?([^"]+)"?/i.exec(cd);
  const extFromType = (blob.type || "").includes("png") ? "png"
    : (blob.type || "").includes("jpeg") ? "jpg"
    : (blob.type || "").includes("webp") ? "webp"
    : "";
  const filename = (match && match[1]) || `${fallbackName || "mockup"}${extFromType ? "." + extFromType : ""}`;
  const objectUrl = URL.createObjectURL(blob);
  return {
    url: objectUrl,
    filename,
    mime: blob.type || "application/octet-stream",
    revoke: () => { try { URL.revokeObjectURL(objectUrl); } catch {} },
  };
}

async function fetchMockupBlob(orderNumber) {
  return fetchBlobFromUrl(
    `${API}/orders/${encodeURIComponent(orderNumber)}/mockup.preview`,
    `mockup-${orderNumber}`
  );
}

// Iter 640 — Cetak massal: retry terbatas (429/5xx/jaringan/gambar rusak) + validasi gambar benar-benar bisa
//   di-decode sebelum masuk dokumen cetak. 4xx lain (404/403) tidak di-retry.
const sleep = (ms) => new Promise(r => setTimeout(r, ms));
async function decodable(url) {
  const im = new Image();
  im.src = url;
  if (im.decode) { await im.decode(); } else { await new Promise((ok, no) => { im.onload = ok; im.onerror = no; }); }
  if (!im.naturalWidth) throw new Error("Gambar kosong");
}
async function fetchWithRetry(fetcher, tries = 4) {
  let last;
  for (let a = 0; a < tries; a++) {
    try {
      const info = await fetcher();
      if (!String(info.mime || "").startsWith("image/")) { info.revoke(); throw Object.assign(new Error("Bukan file gambar"), { status: 415 }); }
      try { await decodable(info.url); } catch (e) { info.revoke(); throw Object.assign(new Error("Gambar gagal dibaca"), { retry: true }); }
      return info;
    } catch (e) {
      last = e;
      const st = e?.status;
      const retryable = e?.retry || !st || st === 429 || st >= 500;
      if (!retryable || a === tries - 1) break;
      await sleep((st === 429 ? 1500 : 700) * Math.pow(2, a) + Math.random() * 300);
    }
  }
  throw last;
}

// Iter 374 — Shopee: ambil blob dari endpoint shopee preview per file (barcode-stamped).
async function fetchShopeeFileBlob(fileId, orderNumber) {
  return fetchBlobFromUrl(
    `${API}/shopee/files/${encodeURIComponent(fileId)}/preview`,
    `mockup-shopee-${orderNumber || fileId}`
  );
}

// Unduh file ke komputer user. TIDAK memanggil mockup-print.
export async function unduhMockup(orderNumber) {
  await unduhDariBlob(() => fetchMockupBlob(orderNumber));
}

// Iter 374 — Unduh Mockup Shopee (per file dari snapshot). TIDAK auto-tandai.
export async function unduhMockupShopee(fileId, orderNumber) {
  await unduhDariBlob(() => fetchShopeeFileBlob(fileId, orderNumber));
}

// Unduh file mockup offline per-file (versi cetak ber-barcode dari /files/{id}/preview). TIDAK menandai cetak.
export async function unduhMockupFile(url, name) {
  await unduhDariBlob(() => fetchBlobFromUrl(url, name));
}

// Helper umum: trigger download dari fungsi fetcher blob.
async function unduhDariBlob(fetcher) {
  const info = await fetcher();
  try {
    const a = document.createElement("a");
    a.href = info.url;
    a.download = info.filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  } finally {
    // Delay revoke sedikit agar browser sempat mulai download.
    setTimeout(info.revoke, 2000);
  }
}

// Buka jendela print dengan layout A5/A4. TIDAK memanggil mockup-print.
// Catatan Safari iOS: `window.open` HARUS dipanggil pada awal click handler sinkron
// (sebelum `await`) agar tidak diblokir pop-up. Kami buka jendela dengan placeholder
// terlebih dahulu, lalu isi dengan HTML + blob URL setelah fetch selesai.
//
// Iter 516 — PRIMARY path: hidden iframe print di SAME PAGE. Tidak butuh pop-up
//   permission sama sekali (Chrome/Edge Windows kadang menolak window.open dengan
//   dimensi `width=900,height=1200` meskipun user gesture valid). Window.open
//   dipakai sbg FALLBACK bila iframe gagal (sangat rare).
//
// Helper: cetak via hidden <iframe srcdoc> di body current window. Mengembalikan
// Promise yang resolve setelah dialog print user TUTUP (afterprint event), atau
// reject bila dokumen cetak tidak siap (semua gambar gagal decode).
//
//   cleanupBlobs: callback untuk revoke blob URLs setelah dialog ditutup.
//   onReadyError: callback saat dokumen tidak siap (semua image gagal). Caller
//                 akan menampilkan pesan error di UI parent (modal).
async function printViaHiddenIframe(htmlDoc, cleanupBlobs) {
  return new Promise((resolve, reject) => {
    if (typeof document === "undefined" || !document.body) {
      reject(new Error("Lingkungan DOM tidak tersedia."));
      return;
    }
    const iframe = document.createElement("iframe");
    iframe.setAttribute("aria-hidden", "true");
    iframe.setAttribute("title", "Dokumen Cetak Mockup");
    iframe.style.cssText = "position:fixed;right:0;bottom:0;width:0;height:0;border:0;visibility:hidden;";
    let settled = false;
    let cleanupTimer = null;
    const removeIframe = () => {
      try { document.body.removeChild(iframe); } catch { /* noop */ }
    };
    const doCleanup = () => {
      try { cleanupBlobs && cleanupBlobs(); } catch { /* noop */ }
      removeIframe();
    };
    const safeResolve = (payload) => {
      if (settled) return; settled = true;
      // Jangan cleanup langsung — tunggu dialog print selesai (afterprint)
      //   atau fallback 10 menit. Jika tidak ada afterprint, user mungkin masih
      //   lingering di preview; blob URL harus tetap hidup.
      resolve(payload);
    };
    const safeReject = (e) => {
      if (settled) return; settled = true;
      doCleanup();
      if (cleanupTimer) clearTimeout(cleanupTimer);
      reject(e);
    };
    iframe.onload = () => {
      let cw = null;
      try { cw = iframe.contentWindow; } catch { /* noop */ }
      if (!cw || !cw.document) {
        safeReject(new Error("Dokumen cetak tidak dapat dibuka. Coba buka ulang dialog."));
        return;
      }
      const imgs = Array.from(cw.document.querySelectorAll("img[data-idx], #mockImg"));
      const waitImg = (im) => new Promise((res) => {
        const done = (err) => res({ err: err || null });
        if (typeof im.decode === "function") {
          const run = () => im.decode().then(() => done(null), (e) => done(e || new Error("decode failed")));
          if (im.complete && im.naturalWidth > 0) run();
          else {
            im.addEventListener("load", run, { once: true });
            im.addEventListener("error", () => done(new Error("image load error")), { once: true });
          }
        } else {
          if (im.complete && im.naturalWidth > 0) done(null);
          else {
            im.addEventListener("load", () => done(null), { once: true });
            im.addEventListener("error", () => done(new Error("image load error")), { once: true });
          }
        }
      });
      Promise.all(imgs.map(waitImg)).then((results) => {
        const total = imgs.length;
        const failed = results.filter((r) => r.err).length;
        // Jika ada gambar tapi SEMUA gagal → tolak, caller tampilkan error di modal.
        if (total > 0 && failed === total) {
          safeReject(new Error(
            total === 1
              ? "Gagal memuat gambar mockup. Pratinjau tidak dibuka."
              : `Semua ${total} mockup gagal dimuat. Pratinjau tidak dibuka.`
          ));
          return;
        }
        // Partial fail: catat tapi tetap cetak sisanya; info dikembalikan ke caller.
        const partialNote = failed > 0 ? `${failed} dari ${total} mockup gagal dimuat. Mencetak sisanya.` : "";
        // Kleanup setelah dialog ditutup. afterprint fires di contentWindow saat
        //   user tutup/cancel/print selesai.
        const onAfterPrint = () => {
          try { cw.removeEventListener("afterprint", onAfterPrint); } catch { /* noop */ }
          // Delay 500ms agar browser sempat generate print job sebelum blob direvoke.
          setTimeout(doCleanup, 500);
        };
        try { cw.addEventListener("afterprint", onAfterPrint); } catch { /* noop */ }
        // Fallback cleanup 10 menit (safety net).
        cleanupTimer = setTimeout(doCleanup, 10 * 60 * 1000);
        // Double rAF untuk jamin paint frame sebelum print.
        const raf = cw.requestAnimationFrame || window.requestAnimationFrame;
        raf.call(cw, () => {
          raf.call(cw, () => {
            try { cw.focus(); cw.print(); safeResolve({ printed: total - failed, failed, note: partialNote }); }
            catch (err) { safeReject(err instanceof Error ? err : new Error("Gagal memanggil print()")); }
          });
        });
      });
    };
    iframe.onerror = () => safeReject(new Error("Gagal memuat dokumen cetak."));
    // Lampirkan ke DOM dulu, lalu set srcdoc untuk trigger onload.
    try { document.body.appendChild(iframe); } catch (e) { safeReject(e); return; }
    try { iframe.srcdoc = htmlDoc; }
    catch { safeReject(new Error("Browser tidak mendukung iframe srcdoc.")); }
  });
}

export async function cetakMockup(orderNumber, paper = "A5") {
  return cetakDariBlob({ fetcher: () => fetchMockupBlob(orderNumber), label: orderNumber, paper });
}

// Iter 374 — Cetak Mockup Shopee (per file).
export async function cetakMockupShopee(fileId, orderNumber, paper = "A5L") {
  return cetakDariBlob({ fetcher: () => fetchShopeeFileBlob(fileId, orderNumber), label: orderNumber || fileId, paper });
}

// Iter 642 — Muat semua mockup batch (retry, paralel 2, urutan dipertahankan).
async function loadBatch(items) {
  const uniq = [];
  const seen = new Set();
  for (const it of items) {
    const num = (it?.orderNumber || "").trim();
    const key = it?.fileId ? `f:${it.fileId}` : `n:${num}`;
    if ((!num && !it?.fileId) || seen.has(key)) continue;
    seen.add(key);
    uniq.push(it);
  }
  const results = new Array(uniq.length);
  let next = 0;
  const worker = async () => {
    while (next < uniq.length) {
      const i = next++;
      const it = uniq[i];
      const num = (it.orderNumber || "").trim();
      try {
        const info = await fetchWithRetry(() => it.fileId ? fetchShopeeFileBlob(it.fileId, num) : fetchMockupBlob(num));
        results[i] = { ok: true, info, label: it.label || num, orderNumber: num, fileId: it.fileId, jobId: it.jobId };
        if (typeof it.onLoaded === "function") it.onLoaded();
      } catch (e) {
        results[i] = { ok: false, orderNumber: num, jobId: it.jobId, fileId: it.fileId, reason: e?.message || "Gagal memuat" };
      }
      if (next % 10 === 0) await sleep(250); // batch kecil: beri jeda agar server/penyimpanan tidak membalas 429
    }
  };
  // Iter 640 — paralel 2 (dulu 4 → penyimpanan objek membalas 429 Too Many Requests saat puluhan gambar).
  await Promise.all(Array.from({ length: Math.min(2, uniq.length) }, worker));
  const infos = results.filter(r => r && r.ok);
  const missing = results.filter(r => r && !r.ok).map(r => ({ orderNumber: r.orderNumber, jobId: r.jobId, fileId: r.fileId, reason: r.reason }));
  if (infos.length === 0) {
    const err = new Error(`Tidak ada mockup yang berhasil dimuat (${missing.length} gambar gagal).`);
    err.missing = missing;
    throw err;
  }
  return { infos, missing };
}

// Iter 642 — Alternatif PDF: halaman PDF benar-benar A5 Landscape (210×148 mm), 1 file = 1 halaman,
//   gambar tidak diputar, contain + dipusatkan. Tidak menandai status apa pun.
export async function unduhBatchPdfA5L(items) {
  const { infos, missing } = await loadBatch(items);
  const W = 210, H = 148, M = 5, MAXPX = 2480;
  const doc = new jsPDF({ orientation: "landscape", unit: "mm", format: "a5", compress: true });
  try {
    for (let k = 0; k < infos.length; k++) {
      const im = new Image();
      im.src = infos[k].info.url;
      await im.decode();
      const sc = Math.min(1, MAXPX / Math.max(im.naturalWidth, im.naturalHeight));
      const cv = document.createElement("canvas");
      cv.width = Math.round(im.naturalWidth * sc); cv.height = Math.round(im.naturalHeight * sc);
      const cx = cv.getContext("2d");
      cx.fillStyle = "#fff"; cx.fillRect(0, 0, cv.width, cv.height); cx.drawImage(im, 0, 0, cv.width, cv.height);
      const r = Math.min((W - 2 * M) / cv.width, (H - 2 * M) / cv.height);
      const w = cv.width * r, h = cv.height * r;
      if (k > 0) doc.addPage("a5", "landscape");
      doc.addImage(cv.toDataURL("image/jpeg", 0.92), "JPEG", (W - w) / 2, (H - h) / 2, w, h);
    }
    doc.save(`mockup-shopee-A5-landscape-${infos.length}-file.pdf`);
  } finally { infos.forEach(({ info }) => info.revoke()); }
  return { printed: infos.length, missing, printedFileIds: infos.map(x => x.fileId).filter(Boolean) };
}

// Rido Shopee — PDF A4 Portrait (210×297 mm). Rotasi per file dari format resmi operator Mockup:
//   A4L/A5L (mendatar) → diputar 90° searah jarum jam; A4P → normal; format lain/kosong → TIDAK dicetak (tanpa tebakan).
//   Gambar contain + dipusatkan (tidak terpotong, proporsi tetap). File asli tidak diubah. Status tidak ditandai.
export const A4_PRINT_TIP = "Printer: Kertas A4 · Portrait · Skala 100% (Actual Size). Orientasi tiap halaman sudah diatur di PDF — tidak perlu mengubah orientasi per pesanan.";
const A4_ROTATE = { A4L: true, A5L: true, A4P: false };
export const a4Rotate = (fmt) => (fmt in A4_ROTATE ? A4_ROTATE[fmt] : null);
// Ukuran & posisi (mm) gambar w×h px (setelah rotasi) di halaman A4 Portrait dengan margin M.
export function a4Fit(w, h, M = 5) {
  const W = 210, H = 297, r = Math.min((W - 2 * M) / w, (H - 2 * M) / h);
  return { x: (W - w * r) / 2, y: (H - h * r) / 2, w: w * r, h: h * r };
}

export async function cetakPdfA4P(items) {
  const known = items.filter(it => a4Rotate(it.paperFormat) !== null);
  const unknown = items.filter(it => a4Rotate(it.paperFormat) === null)
    .map(it => ({ orderNumber: it.orderNumber, jobId: it.jobId, fileId: it.fileId, reason: "Format Belum Dipastikan — tidak dicetak A4" }));
  if (!known.length) throw Object.assign(new Error("Format file belum dipastikan operator Mockup — tidak dicetak A4. Pastikan format terlebih dahulu."), { missing: unknown });
  const fmtOf = Object.fromEntries(known.map(it => [it.fileId, it.paperFormat]));
  const { infos, missing } = await loadBatch(known);
  const MAXPX = 3508;
  const doc = new jsPDF({ orientation: "portrait", unit: "mm", format: "a4", compress: true });
  const pages = [];
  try {
    for (let k = 0; k < infos.length; k++) {
      const im = new Image();
      im.src = infos[k].info.url;
      await im.decode();
      const rot = a4Rotate(fmtOf[infos[k].fileId]);
      const sc = Math.min(1, MAXPX / Math.max(im.naturalWidth, im.naturalHeight));
      const iw = Math.round(im.naturalWidth * sc), ih = Math.round(im.naturalHeight * sc);
      const cv = document.createElement("canvas");
      cv.width = rot ? ih : iw; cv.height = rot ? iw : ih;
      const cx = cv.getContext("2d");
      cx.fillStyle = "#fff"; cx.fillRect(0, 0, cv.width, cv.height);
      if (rot) { cx.translate(ih, 0); cx.rotate(Math.PI / 2); }
      cx.drawImage(im, 0, 0, iw, ih);
      const f = a4Fit(cv.width, cv.height);
      if (k > 0) doc.addPage("a4", "portrait");
      doc.addImage(cv.toDataURL("image/jpeg", 0.92), "JPEG", f.x, f.y, f.w, f.h);
      pages.push({ fileId: infos[k].fileId, rotated: rot });
    }
    const url = URL.createObjectURL(doc.output("blob"));
    const w = window.open(url, "_blank");
    if (!w) doc.save(`mockup-shopee-A4-portrait-${infos.length}-file.pdf`);
    setTimeout(() => { try { URL.revokeObjectURL(url); } catch {} }, 10 * 60 * 1000);
  } finally { infos.forEach(({ info }) => info.revoke()); }
  return { printed: infos.length, missing: [...missing, ...unknown], printedFileIds: infos.map(x => x.fileId).filter(Boolean), pages };
}

// Offline massal — PDF A5 Portrait (148×210 mm), 1 mockup = 1 halaman, urutan dipertahankan.
//   Gambar mendatar diputar 90° berlawanan jarum jam (sama dgn PDF Mockup + Resi Shopee), contain + dipusatkan.
export function a5pFit(w, h, M = 5) {
  const W = 148, H = 210, r = Math.min((W - 2 * M) / w, (H - 2 * M) / h);
  return { x: (W - w * r) / 2, y: (H - h * r) / 2, w: w * r, h: h * r };
}

export async function cetakPdfA5P(items) {
  const { infos, missing } = await loadBatch(items);
  const MAXPX = 2480;
  const doc = new jsPDF({ orientation: "portrait", unit: "mm", format: "a5", compress: true });
  try {
    for (let k = 0; k < infos.length; k++) {
      const im = new Image();
      im.src = infos[k].info.url;
      await im.decode();
      const rot = im.naturalWidth > im.naturalHeight;
      const sc = Math.min(1, MAXPX / Math.max(im.naturalWidth, im.naturalHeight));
      const iw = Math.round(im.naturalWidth * sc), ih = Math.round(im.naturalHeight * sc);
      const cv = document.createElement("canvas");
      cv.width = rot ? ih : iw; cv.height = rot ? iw : ih;
      const cx = cv.getContext("2d");
      cx.fillStyle = "#fff"; cx.fillRect(0, 0, cv.width, cv.height);
      if (rot) { cx.translate(0, iw); cx.rotate(-Math.PI / 2); }
      cx.drawImage(im, 0, 0, iw, ih);
      const f = a5pFit(cv.width, cv.height);
      if (k > 0) doc.addPage("a5", "portrait");
      doc.addImage(cv.toDataURL("image/jpeg", 0.92), "JPEG", f.x, f.y, f.w, f.h);
    }
    const url = URL.createObjectURL(doc.output("blob"));
    const w = window.open(url, "_blank");
    if (!w) doc.save(`mockup-offline-A5-portrait-${infos.length}-nota.pdf`);
    setTimeout(() => { try { URL.revokeObjectURL(url); } catch {} }, 10 * 60 * 1000);
  } finally { infos.forEach(({ info }) => info.revoke()); }
  return { printed: infos.length, missing, pages: infos.length };
}

// Iter 394 — Cetak BATCH beberapa nota (Rido) dalam SATU print job.
//   `items`: Array<{ orderNumber: string, label?: string }> — grouping sudah dilakukan caller
//            (1 nota = 1 entry). Mockup Offline per nota ambil dari /orders/{num}/mockup.preview.
//   `paper`: "A5" | "A4" (default A5). Format cetak reuse layout existing.
//   Return: { printed: number, missing: Array<{orderNumber, reason}> }.
//   Catatan: fetch dilakukan sekuensial agar sopan ke backend; browser print dialog
//            hanya dibuka SEKALI setelah semua gambar siap. Tidak memanggil mockup-print
//            endpoint (penandaan Sudah Dicetak tetap via alur konfirmasi existing).
export async function cetakBatchMockup(items, paper = "A5") {
  const preset = PAPER_PRESETS[paper] || PAPER_PRESETS.A5;
  // Iter 516 — Fetch dulu (sync-await), lalu print via hidden iframe di SAME PAGE.
  //   Tidak butuh pop-up permission → Chrome/Edge Windows tidak akan blokir.
  //   Fallback window.open bila iframe gagal (sangat rare).
  // Iter 637 — item boleh membawa `fileId` (mockup Shopee per file) → kunci unik per file, bukan per nota.
  //   Fetch paralel terbatas (4) agar puluhan pesanan tidak membuat aplikasi macet; urutan dipertahankan.
  const { infos, missing } = await loadBatch(items);
  const printedFileIds = infos.map(x => x.fileId).filter(Boolean);
  const pc = pageCss(preset);
  const boxW = pc.bw;
  const boxH = pc.bh;
  const pagesHtml = infos.map(({ info, label, orderNumber }, i) => `
    <div class="page" data-order="${escapeAttr(orderNumber)}">
      <div class="mockup-box">
        <img src="${escapeAttr(info.url)}" alt="Mockup ${escapeHtml(label)}" data-idx="${i}"/>
      </div>
    </div>
  `).join("");
  const missingBanner = missing.length > 0
    ? `<div class="screen-hint" role="note" style="background:#fee2e2;color:#7f1d1d;border-color:#fecaca">
         <b>${missing.length} nota tidak memiliki mockup dan dilewati:</b>
         ${escapeHtml(missing.map(m => m.orderNumber).join(", "))}
       </div>`
    : "";
  const htmlDoc = `<!doctype html>
<html lang="id">
<head>
<meta charset="utf-8"/>
<title>Cetak Mockup — ${infos.length} nota</title>
<style>
  @page { size: ${pc.size}; margin: 0; }
  * { box-sizing: border-box; }
  html, body { margin: 0; padding: 0; background: #fff; }
  body { font-family: system-ui, Arial, sans-serif; color: #111; }
  .page {
    ${pc.box}
    padding: ${pc.safe}mm;
    position: relative;
    page-break-after: always; break-after: page; page-break-inside: avoid; break-inside: avoid;
    display: flex; align-items: ${pc.align}; justify-content: ${pc.align};
  }
  .page:last-of-type { page-break-after: auto; break-after: auto; }
  .mockup-box {
    width: ${boxW}mm;
    height: ${boxH}mm;
    display: flex;
    align-items: ${pc.align};
    justify-content: ${pc.align};
    overflow: hidden;
  }
  .mockup-box img {
    display: block;
    max-width: 100%;
    max-height: 100%;
    width: auto;
    height: auto;
    object-fit: contain;
  }
  .screen-hint {
    position: fixed;
    top: 10px; left: 10px; right: 10px;
    background: #fef3c7;
    color: #78350f;
    border: 1px solid #fcd34d;
    border-radius: 8px;
    padding: 10px 12px;
    font-size: 13px;
    line-height: 1.4;
    z-index: 999;
  }
  .screen-hint b { color: inherit; }
  .screen-hint .actions { margin-top: 6px; display: flex; gap: 8px; flex-wrap: wrap; }
  .screen-hint button {
    background: #0f172a; color: #fff; border: 0; border-radius: 6px;
    padding: 6px 12px; font-size: 12px; font-weight: 700; cursor: pointer;
  }
  .error-banner {
    position: fixed;
    top: 10px; left: 10px; right: 10px;
    background: #fee2e2; color: #7f1d1d; border: 1px solid #fca5a5;
    border-radius: 8px; padding: 10px 12px; font-size: 13px; line-height: 1.4;
    z-index: 1000; display: none;
  }
  @media print {
    .screen-hint { display: none !important; }
    .error-banner { display: none !important; }
  }
</style>
</head>
<body>
  <div class="screen-hint" role="note">
    <b>Kertas ${preset.exact ? "A5 Landscape" : "A4"} · Skala 100% · Header/footer nonaktif.</b> Mode: <b>${escapeHtml(preset.label)}</b>.
    <b>${infos.length} nota</b> siap cetak dalam satu print job.
    <div class="actions">
      <button type="button" onclick="window.print()">Cetak sekarang</button>
      <button type="button" onclick="window.close()" style="background:#64748b">Tutup</button>
    </div>
  </div>
  <div class="error-banner" id="errBanner" role="alert"></div>
  ${missingBanner}
  ${pagesHtml}
  <!-- Iter 516 — Inline script auto-print DIHAPUS. Logic ready-check + double rAF +
       window.print() sekarang di printViaHiddenIframe (parent) agar iframe cukup
       render HTML saja dan loadable di hidden mode. -->
</body>
</html>`;
  try {
    // Iter 516 — PRIMARY: hidden iframe di same-page (no pop-up permission).
    const cleanupInfos = () => { infos.forEach(({ info }) => { try { info.revoke(); } catch { /* noop */ } }); };
    await printViaHiddenIframe(htmlDoc, cleanupInfos);
    return { printed: infos.length, missing, printedFileIds };
  } catch (iframeErr) {
    // Iter 516 — FALLBACK: window.open (user harus izinkan pop-up).
    //   Hanya tereksekusi bila iframe strcdoc / print() gagal (sangat rare).
    //   Jika pop-up diblokir, tampilkan error dengan guidance.
    infos.forEach(({ info }) => { try { info.revoke(); } catch { /* noop */ } });
    // Bila error dari ready-check (semua gambar gagal), teruskan apa adanya.
    if (iframeErr && /mockup gagal dimuat|Gagal memuat gambar/i.test(iframeErr.message)) {
      throw iframeErr;
    }
    throw new Error("Gagal menyiapkan dokumen cetak. Coba muat ulang halaman lalu ulangi.");
  }
}

// Helper umum: cetak via hidden iframe dari fetcher blob (Iter 516).
//   Sebelumnya pakai window.open yang kadang diblokir Chrome/Edge Windows meskipun
//   user gesture valid (dimensi `width=900,height=1200` memicu popup blocker
//   level-strict). Iframe di same-page tidak butuh pop-up permission.
async function cetakDariBlob({ fetcher, label, paper = "A5" }) {
  const preset = PAPER_PRESETS[paper] || PAPER_PRESETS.A5;
  let info;
  try {
    info = await fetcher();
  } catch (e) {
    throw e;
  }
  const pc = pageCss(preset);
  const boxW = pc.bw;
  const boxH = pc.bh;
  const htmlDoc = `<!doctype html>
<html lang="id">
<head>
<meta charset="utf-8"/>
<title>Cetak Mockup ${escapeHtml(label)}</title>
<style>
  @page { size: ${pc.size}; margin: 0; }
  * { box-sizing: border-box; }
  html, body { margin: 0; padding: 0; background: #fff; }
  body { font-family: system-ui, Arial, sans-serif; color: #111; }
  .page {
    ${pc.box}
    padding: ${pc.safe}mm;
    position: relative;
    display: flex; align-items: ${pc.align}; justify-content: ${pc.align};
  }
  .mockup-box {
    width: ${boxW}mm;
    height: ${boxH}mm;
    display: flex;
    align-items: ${pc.align};
    justify-content: ${pc.align};
    overflow: hidden;
  }
  .mockup-box img {
    display: block;
    max-width: 100%;
    max-height: 100%;
    width: auto;
    height: auto;
    object-fit: contain;
  }
  .screen-hint {
    position: fixed;
    top: 10px; left: 10px; right: 10px;
    background: #fef3c7;
    color: #78350f;
    border: 1px solid #fcd34d;
    border-radius: 8px;
    padding: 10px 12px;
    font-size: 13px;
    line-height: 1.4;
    z-index: 999;
  }
  .screen-hint b { color: #78350f; }
  .screen-hint .actions { margin-top: 6px; display: flex; gap: 8px; flex-wrap: wrap; }
  .screen-hint button {
    background: #0f172a; color: #fff; border: 0; border-radius: 6px;
    padding: 6px 12px; font-size: 12px; font-weight: 700; cursor: pointer;
  }
  .error-banner {
    position: fixed;
    top: 10px; left: 10px; right: 10px;
    background: #fee2e2; color: #7f1d1d; border: 1px solid #fca5a5;
    border-radius: 8px; padding: 10px 12px; font-size: 13px; line-height: 1.4;
    z-index: 1000; display: none;
  }
  @media print {
    .screen-hint { display: none !important; }
    .error-banner { display: none !important; }
    .page { padding: ${pc.safe}mm; }
  }
</style>
</head>
<body>
  <div class="screen-hint" role="note">
    <b>Kertas ${preset.exact ? "A5 Landscape" : "A4"} · Skala 100% · Header/footer nonaktif.</b><br/>
    Mode penyajian: <b>${escapeHtml(preset.label)}</b>. ${escapeHtml(preset.desc)}
    <div class="actions">
      <button type="button" onclick="window.print()">Cetak sekarang</button>
      <button type="button" onclick="window.close()" style="background:#64748b">Tutup</button>
    </div>
  </div>
  <div class="error-banner" id="errBanner" role="alert"></div>
  <div class="page">
    <div class="mockup-box">
      <img id="mockImg" src="${escapeAttr(info.url)}" alt="Mockup ${escapeHtml(label)}"/>
    </div>
  </div>
  <!-- Iter 516 — Inline script auto-print DIHAPUS. Logic ready-check sekarang
       di printViaHiddenIframe (parent). -->
</body>
</html>`;
  try {
    // Iter 516 — PRIMARY: hidden iframe (no pop-up permission).
    const cleanupInfo = () => { try { info.revoke(); } catch { /* noop */ } };
    await printViaHiddenIframe(htmlDoc, cleanupInfo);
  } catch (iframeErr) {
    try { info.revoke(); } catch { /* noop */ }
    // Teruskan error ready-check (semua image gagal decode) apa adanya ke caller
    //   (CetakMockupSizeModal.doPrint akan menampilkan pesan di setErr).
    if (iframeErr && /gagal dimuat|Gagal memuat gambar/i.test(iframeErr.message)) {
      throw iframeErr;
    }
    throw new Error("Gagal menyiapkan dokumen cetak. Coba muat ulang halaman lalu ulangi.");
  }
}

function escapeHtml(s) {
  return String(s || "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#039;");
}
function escapeAttr(s) { return escapeHtml(s); }

// ---------------------------------------------------------------------------
// Modal pilihan A5/A4. Default A5. Tombol Cetak membuka jendela cetak.
// `onPrint(paper)` dan `onDownload()` adalah callback async dari parent agar
// komponen ini bisa dipakai Offline (preview nota) maupun Shopee (per file).
// Fallback: bila `orderNumber` prop tersedia dan callback tidak diberikan,
// komponen tetap bekerja untuk alur Offline existing (kompatibilitas Iter 373).
// ---------------------------------------------------------------------------
export const A5L_PRINT_TIP = "Dokumen sudah diatur A5 Landscape, jadi Chrome otomatis memakai orientasi mendatar. Di dialog cetak cukup pastikan: Skala 100% (Ukuran sebenarnya), Margin Tidak ada/Default, Header & footer nonaktif. Bila printer tetap salah orientasi, pakai tombol Unduh PDF A5 Landscape.";

export function CetakMockupSizeModal({ orderNumber, title, onClose, onPrint, onDownload, presets }) {
  const keys = presets && presets.length ? presets : ["A5", "A4"];
  const [paper, setPaper] = useState(keys[0]);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const doPrint = async () => {
    setBusy(true); setErr("");
    try {
      if (onPrint) {
        await onPrint(paper);
      } else {
        await cetakMockup(orderNumber, paper);
      }
      onClose?.();
    } catch (e) {
      setErr(e?.message || "Gagal membuka dialog cetak.");
    } finally {
      setBusy(false);
    }
  };
  const doDownload = async () => {
    setBusy(true); setErr("");
    try {
      if (onDownload) {
        await onDownload();
      } else {
        await unduhMockup(orderNumber);
      }
      onClose?.();
    } catch (e) {
      setErr(e?.message || "Gagal mengunduh mockup.");
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="cm-backdrop" role="dialog" aria-modal="true" aria-label="Pilih ukuran penyajian mockup"
      onClick={onClose} data-testid="cetak-size-modal">
      <div className="cm-panel" onClick={e => e.stopPropagation()}>
        <header className="cm-head">
          <h3>Cetak Mockup — {title || `Nota ${orderNumber || ""}`}</h3>
          <button type="button" className="cm-close" onClick={onClose} aria-label="Tutup" data-testid="cetak-size-close">
            <X size={16}/>
          </button>
        </header>
        <div className="cm-body">
          <p className="cm-hint">
            <b>Kertas {PAPER_PRESETS[paper]?.exact ? "A5 Landscape" : PAPER_PRESETS[paper]?.pdf ? "A4 Portrait" : "A4"} · Skala 100% · Header/footer nonaktif.</b><br/>
            {PAPER_PRESETS[paper]?.exact ? A5L_PRINT_TIP : PAPER_PRESETS[paper]?.pdf ? A4_PRINT_TIP : <>A5/A4 di sini adalah <b>ukuran penyajian dokumen</b>, bukan ukuran fisik desain bordir.
            Kertas fisik tetap A4 — pilihan A5 menempatkan mockup di area A5 landscape pojok kiri atas.</>}
          </p>
          <div className="cm-size-group" role="radiogroup" aria-label="Ukuran penyajian">
            {keys.map(k => (
              <label key={k} className={`cm-size-opt ${paper === k ? "active" : ""}`} data-testid={`cetak-size-${k.toLowerCase()}`}>
                <input
                  type="radio"
                  name="paper"
                  value={k}
                  checked={paper === k}
                  onChange={() => setPaper(k)}
                />
                <span className="cm-size-label">
                  <strong>{PAPER_PRESETS[k].label}</strong>
                  <small>{PAPER_PRESETS[k].desc}</small>
                </span>
              </label>
            ))}
          </div>
          {err && (
            <div className="cm-err" data-testid="cetak-size-err">
              <AlertCircle size={14}/> <span>{err}</span>
            </div>
          )}
        </div>
        <footer className="cm-foot">
          <button type="button" className="cm-btn cm-btn-ghost" onClick={doDownload} disabled={busy}
            data-testid="cetak-size-download">
            <Download size={14}/> Unduh Mockup
          </button>
          <button type="button" className="cm-btn cm-btn-primary" onClick={doPrint} disabled={busy}
            data-testid="cetak-size-print">
            <Printer size={14}/> {busy ? "Memuat…" : "Cetak"}
          </button>
        </footer>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Modal konfirmasi tandai mockup dicetak. Tombol muncul tanpa wajib klaim.
// Preview/unduh/dialog cetak TIDAK auto-tandai. Hanya "Ya, sudah dicetak"
// memanggil onConfirm (yang nanti call POST mockup-print di parent).
// ---------------------------------------------------------------------------
export function KonfirmasiCetakModal({ orderNumber, scopeLabel, pendingCount, totalCount, onCancel, onConfirm }) {
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const doConfirm = async () => {
    setBusy(true); setErr("");
    try {
      await onConfirm();
    } catch (e) {
      setErr(e?.message || "Gagal menandai mockup dicetak.");
      setBusy(false);
      return;
    }
    setBusy(false);
    // Close dilakukan oleh parent setelah onConfirm sukses.
  };
  return (
    <div className="cm-backdrop" role="dialog" aria-modal="true" aria-label="Konfirmasi mockup dicetak"
      onClick={busy ? undefined : onCancel} data-testid="cetak-confirm-modal">
      <div className="cm-panel cm-panel-sm" onClick={e => e.stopPropagation()}>
        <header className="cm-head">
          <h3>Mockup sudah selesai dicetak?</h3>
        </header>
        <div className="cm-body">
          <p className="cm-hint">
            {scopeLabel || "Nota"} <b>{orderNumber}</b>: <b>{pendingCount}</b> dari {totalCount} mockup akan ditandai sudah dicetak.<br/>
            Penandaan hanya mencatat identitas petugas dan waktu — tidak otomatis ACC, memajukan tahap, atau mengurangi stok.
          </p>
          {err && (
            <div className="cm-err" data-testid="cetak-confirm-err">
              <AlertCircle size={14}/> <span>{err}</span>
            </div>
          )}
        </div>
        <footer className="cm-foot">
          <button type="button" className="cm-btn cm-btn-ghost" onClick={onCancel} disabled={busy}
            data-testid="cetak-confirm-cancel">Batal</button>
          <button type="button" className="cm-btn cm-btn-primary" onClick={doConfirm} disabled={busy}
            data-testid="cetak-confirm-ok">
            <FileText size={14}/> {busy ? "Menyimpan…" : "Ya, sudah dicetak"}
          </button>
        </footer>
      </div>
    </div>
  );
}
